
import { ReactNode } from "react";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";

interface Step {
  title: string;
  description: string;
  icon?: ReactNode;
}

interface ProcessSectionProps {
  title: string;
  subtitle?: string;
  description?: string;
  steps: Step[];
  className?: string;
  layout?: "horizontal" | "vertical";
  background?: "white" | "light";
  id?: string; // Added id prop
}

export const ProcessSection = ({
  title,
  subtitle,
  description,
  steps,
  className,
  layout = "horizontal",
  background = "white",
  id
}: ProcessSectionProps) => {
  const getBgClasses = () => {
    return background === "light" ? "bg-gray-50" : "bg-white";
  };

  return (
    <section id={id} className={cn("py-16 md:py-24", getBgClasses(), className)}>
      <Container>
        <div className="text-center mb-12">
          {subtitle && (
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 font-medium text-sm mb-4">
              {subtitle}
            </div>
          )}
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6">
            {title}
          </h2>
          {description && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>

        {layout === "horizontal" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center">
                  <div className="rounded-full bg-purple-100 w-12 h-12 flex items-center justify-center mb-4">
                    {step.icon ? (
                      step.icon
                    ) : (
                      <span className="text-purple-700 font-bold text-lg">{index + 1}</span>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900 text-center">{step.title}</h3>
                  <p className="text-gray-600 text-center">{step.description}</p>
                </div>
                
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-gray-200 -z-10 transform -translate-x-1/2" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-12 max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="flex space-x-6">
                <div className="flex-shrink-0">
                  <div className="rounded-full bg-purple-100 w-12 h-12 flex items-center justify-center">
                    {step.icon ? (
                      step.icon
                    ) : (
                      <span className="text-purple-700 font-bold text-lg">{index + 1}</span>
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="h-full w-0.5 bg-gray-200 mx-auto mt-4" />
                  )}
                </div>
                <div className="pt-2">
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
};
